import { useEffect, useMemo } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import _ from "lodash"
import { featureCollection } from "@turf/helpers"

import { useVenue } from "providers/venue"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import {
  getSuitablyValueBetweenBearings,
  getLocationIdByFeature,
  getRelatedLocationIdsByFeature,
  getDuplexGeometryIdsByFeature,
} from "components/IndoorMap"

import { IRoute } from "interfaces"

const DEFAULT_OPTIONS = {
  offset: { top: 0, left: 0, right: 0, bottom: 0 },
  enabled: true,
}

export const useDirectionOverviewMap = (
  route: IRoute | null,
  ordinal: number,
  options = DEFAULT_OPTIONS
): void => {
  const { indoorRef, mapLoaded } = useIndoorMap()
  const {
    origin: originParams,
    destination: destinationParams,
    step,
  } = useParams()
  const [searchParams] = useSearchParams()
  const { offset, enabled } = { ...DEFAULT_OPTIONS, ...options }
  const { bottom, top, left, right } = offset

  const offsetOption = useMemo(
    () => ({ bottom, top, left, right }),
    [bottom, top, left, right]
  )

  const {
    relationshipGraphLoaded,
    dataLoaded,
    transformStepsToGeometries,
    transformLocationToFeature,
  } = useVenue()

  const targetOrigin = useMemo(
    () => transformLocationToFeature(originParams),
    [originParams, transformLocationToFeature]
  )

  const targetDestination = useMemo(
    () => transformLocationToFeature(destinationParams),
    [destinationParams, transformLocationToFeature]
  )

  const mapReady =
    indoorRef?.current && mapLoaded && dataLoaded && relationshipGraphLoaded

  const navigationGeometries = useMemo(
    () => route && route.steps && transformStepsToGeometries(route.steps),
    [route, transformStepsToGeometries]
  )

  // Effect#1: Prepare indoor map before drawing route path
  useEffect(() => {
    const indoorMap = indoorRef?.current
    if (indoorMap && mapLoaded) {
      indoorMap.hideVenueObjects()
      indoorMap.disableClick()
      indoorMap.setFeatureObject3DsOpacity(1)
      indoorMap.setSpriteMarkersOpacity(1)
    }
  }, [indoorRef, mapLoaded])

  // Effect#2: Create new route path on indoor map
  useEffect(() => {
    const indoorMap = indoorRef?.current
    if (indoorMap && mapReady && navigationGeometries && route) {
      indoorMap.clearNavigationGeometries()
      // Create route path on map
      indoorMap.createNavigationGeometries(
        navigationGeometries,
        route.destination
      )
      indoorMap.clearHighlightElements()
      indoorMap.clearHighlightObject()
    }
  }, [indoorRef, navigationGeometries, mapReady, route])

  // Effect#3: Adjust camera view to focus on current overview route on the current floor
  useEffect(() => {
    const indoorMap = indoorRef?.current
    const isReadyToFocusOnRoute = mapReady && enabled && route
    if (isReadyToFocusOnRoute) {
      indoorMap.changeLevelByOrdinal(ordinal)

      // Highlight the destination 3D object if it has a located unit.
      indoorMap.clearHighlightObject()
      // Prepare target destination element IDs (if existing) to highlight (unit, kiosk, amenity, or occupant).
      // filter for prevent setHighlightedObject function create occupant highlight marker
      const ignoreTargetId =
        targetDestination?.feature_type &&
        ["geolocation", "occupant"].includes(targetDestination?.feature_type)
      const destElementIds = _.compact(
        _.uniq([
          getLocationIdByFeature(targetDestination),
          ...getRelatedLocationIdsByFeature(targetDestination),
          ignoreTargetId ? null : targetDestination?.id,
        ])
      ) //Find new solution to refactor SetHighlight Element object

      if (destElementIds.length > 0)
        indoorMap.setHighlightedObject(destElementIds)

      // Find Navigation Geometries in this ordinal
      const navGeoInOrdinal = navigationGeometries.filter(
        (geojson) => geojson?.properties?.ordinal === ordinal
      )

      if (!navGeoInOrdinal?.length) return

      const navigationCollection = featureCollection(navGeoInOrdinal)

      const extent = indoorMap.getFeatureExtent(navigationCollection)
      const center = indoorMap.getExtentCenter(extent)
      const zoom = indoorMap.getExtentZoom(extent)
      const currentBearing = indoorMap.getBearing()
      const defaultBearing = indoorMap.defaultBearing
      const bearing = getSuitablyValueBetweenBearings(
        defaultBearing,
        currentBearing
      )

      const newCenter = indoorMap.getTargetViewCenter(
        {
          bearing,
          center,
          pitch: 0,
          zoom: zoom - 0.5,
        },
        { offset: offsetOption }
      )

      // Fly to and display overview step path by current ordinal
      indoorMap.flyTo(newCenter, {
        bearing,
        duration: 600,
        easing: "out",
        pitch: 0,
        zoom: zoom - 0.5,
      })

      // TODO: improve logic to show origin logo marker for case both, occupant render_type === logo (mobile)
      // hide origin and destination logo / text marker prevent overlap marker
      const destDuplexIds = getDuplexGeometryIdsByFeature(route.destination)
      const destElementIdsToHide = _.compact([
        route.destination.id,
        ...destDuplexIds,
      ])

      indoorRef.current.hideGeometryByElementIds(destElementIdsToHide)

      const originDuplexIds = getDuplexGeometryIdsByFeature(route.origin)
      const originElementIdsToHide = _.compact([
        route.origin.id,
        ...originDuplexIds,
      ])
      indoorRef.current.hideGeometryByElementIds(originElementIdsToHide)
    }
  }, [
    enabled,
    indoorRef,
    mapReady,
    navigationGeometries,
    ordinal,
    route,
    searchParams,
    step,
    targetDestination,
    targetOrigin,
    transformStepsToGeometries,
    offsetOption,
  ])

  // Effect#4: When only one of origin or destination remains in the input, focus on remaining location
  useEffect(() => {
    const indoorMap = indoorRef?.current
    const isViewingOneLocation =
      _.compact([targetOrigin, targetDestination]).length === 1
    const isReadyToHighlight = indoorMap && mapReady && isViewingOneLocation

    if (!isReadyToHighlight) return

    const targetFeature = targetOrigin || targetDestination
    const originElementIds = _.compact(
      _.uniq([
        getLocationIdByFeature(targetOrigin),
        targetOrigin?.id,
        ...getRelatedLocationIdsByFeature(targetOrigin),
      ])
    )
    const destElementIds = _.compact(
      _.uniq([
        getLocationIdByFeature(targetDestination),
        targetDestination?.id,
        ...getRelatedLocationIdsByFeature(targetDestination),
      ])
    )

    // Prepare target element IDs to highlight (unit, kiosk, amenity, or occupant).
    const elementIdsToHighlight =
      originElementIds.length > 0 ? originElementIds : destElementIds

    // Clear path in the route path layer on the map and all highlights
    indoorMap.clearNavigationGeometries()
    indoorMap.clearHighlightElements()
    indoorMap.clearHighlightObject()
    // Change floor to the targeted location's ordinal.
    indoorMap.changeLevelByOrdinal(
      Number(targetFeature?.properties?.ordinal || 0)
    )

    const highlightedFeature = indoorMap.setHighlightElementIds(
      elementIdsToHighlight
    )

    // Ignore camera view adjustment if no elements are highlighted.
    if (!highlightedFeature) return

    // Move camera view to the remaining target location.
    const extent = indoorMap.getFeatureExtent(highlightedFeature)
    const center = indoorMap.getExtentCenter(extent)
    const zoom = indoorMap.getExtentZoom(extent)
    indoorMap.flyTo(center, { zoom })
  }, [indoorRef, mapReady, targetDestination, targetOrigin])
}
